import React from "react";

import { usePvGeoJson } from "@inrange/api-clients";
import {
  useNetworkSiteList,
  useOrgListUsers,
  useOrganisation,
  useSite,
  useSites,
} from "@inrange/building-manager-api-client";
import { trackUserInteraction } from "@inrange/building-manager-api-client/events";
import {
  KindeUser,
  Organisation,
} from "@inrange/building-manager-api-client/models-organisation";
import { Site } from "@inrange/building-manager-api-client/models-site";
import {
  BreadCrumb,
  ErrorSidebar,
  Loaded,
  Loading,
  ShareAttributes,
  SidebarWithHeader,
  SimpleButton,
  SiteViewHeader,
  getScoreBySiteID,
  isSiteOwnerOccupied,
  saveSessionStoredValue,
} from "@inrange/theme-components";
import {
  siteCanBuy,
  siteCanOnlySellThroughWire,
  siteCanSell,
  siteHasExistingWiredMatch,
} from "@inrange/theme-components/marketplace";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import * as Sentry from "@sentry/react";
import { useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { UserContext } from "../auth/UserContext";
import { countSitesByOwnershipAndOperationalStatus } from "./dashboard/aggregationCalculations";
import EnergySavings from "./SiteViewTabs/EnergySavings";
import FileList from "./SiteViewTabs/FileList";
import { isDemoModeEnabled } from "./SiteViewTabs/demoMode";
import OperationalSummaryLandlord from "./SiteViewTabs/OperationalSummaryLandlord";
import OperationalSummaryOwnerOccupier from "./SiteViewTabs/OperationalSummaryOwnerOccupier";
import OperationalSummaryTenant from "./SiteViewTabs/OperationalSummaryTenant";
import ProposalSummary from "./SiteViewTabs/ProposalSummary";
import PvSystemView from "./SiteViewTabs/PvSystem";
import SiteDocuments from "./SiteViewTabs/SiteDocuments";

const SiteView: React.FC = () => {
  const { logout } = useKindeAuth();
  const doLogout = () => {
    if (import.meta.env.PROD) {
      Sentry.setUser(null);
    }
    logout();
  };

  const location = useLocation();
  const { orgId, siteId } = useParams<{ orgId: string; siteId: string }>();
  const { fetchOrganisation } = useOrganisation(orgId, orgId);
  const { user } = useContext(UserContext);
  const { fetchSite, fetchSiteReport } = useSite({
    siteId,
    userOrgId: orgId,
    app: "customer",
  });
  // Prefetch network sites so that when the user clicks through to the marketplace
  // the data is already loaded
  const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;
  useNetworkSiteList(
    orgId,
    fetchOrganisation.isSuccess && fetchSite.isSuccess,
    testMode,
    orgId
  );

  const [investmentModel, setInvestmentModel] = useState<string | undefined>(
    fetchSite.data?.site?.activeInvestmentModel
  );

  const organisation = fetchOrganisation.data?.organisation;
  const organisationSiteListSites = fetchOrganisation.data?.sites;
  const site = fetchSite.data?.site;
  // Prefetch panel data
  usePvGeoJson(
    siteId,
    site?.pvPanel,
    site?.buildings.map((b) => b.id).join(",")
  );
  // Trigger fetching of all sites for the organisation eagerly so that they are more
  // likely to be pre-loaded when the user navigates to any of them
  useSites(
    (fetchOrganisation.isSuccess && organisation && fetchSite.isSuccess
      ? organisation.siteOwnerships
      : []
    ).map((so) => so.siteID!),
    orgId
  );

  const isOwnerOccupier = isSiteOwnerOccupied(site?.siteOwnerships ?? []);
  const orgType = organisation?.orgType;

  const shareOrgType = isOwnerOccupier
    ? undefined
    : orgType === "Tenant"
      ? "landlord"
      : "tenant";
  const shareOrgData = site?.siteOwnerships
    .filter((so) => !so.isTest)
    .filter((ownershipData) => ownershipData.ownership === shareOrgType);
  const shareOrgIds = shareOrgData
    ? shareOrgData.map((data) => data.orgID)
    : [];
  const {
    orgListUsers: shareOrgUsersQueries,
    addOrganisationUser: addShareOrgUser,
  } = useOrgListUsers(
    // We compute shareOrgIds from the site record, so this request wont happen until
    // after the site is loaded
    shareOrgIds,
    siteId!,
    orgId
  );
  const { addOrganisationUser: addCurrentOrgUser } = useOrgListUsers(
    [orgId!],
    // We don't need to pass the siteId to be allowed to get the current user's org's users
    undefined,
    orgId
  );

  useEffect(() => {
    document.body.scrollTop = 0;
    if (site && !investmentModel) {
      setInvestmentModel(site.activeInvestmentModel);
    }
  }, [site, investmentModel]);

  if (fetchOrganisation.isError) {
    return (
      <ErrorSidebar onClickLogout={doLogout}>
        <Loaded>Organisation not found</Loaded>
      </ErrorSidebar>
    );
  }

  if (fetchSite.isError) {
    return (
      <ErrorSidebar onClickLogout={doLogout}>
        <Loaded>
          Site not found
          <SimpleButton
            fontWeight="600"
            label="Go to dashboard"
            to={`/org/${orgId}/dashboard`}
          />
        </Loaded>
      </ErrorSidebar>
    );
  }

  if (
    fetchOrganisation.isLoading ||
    !organisation ||
    !organisationSiteListSites ||
    fetchSite.isLoading ||
    !site ||
    !investmentModel
  ) {
    return <Loading label="Loading your data..." />;
  }

  const currentUrl = location.pathname;
  const rootPath = `/org/${orgId}/`;

  const siteCounts = countSitesByOwnershipAndOperationalStatus(
    organisationSiteListSites,
    organisation.siteOwnerships
  );
  const isOperational = site.operationalStatus.startsWith("operational");

  const orgName = organisation?.name;
  const siteName = site?.name;
  document.title = orgName
    ? `InRange - ${orgName} - ${siteName}`
    : document.title;

  const irr =
    site.financialModels[isOwnerOccupier ? "ownerOccupier" : "landlord"].license
      .irr;

  const paybackMonths =
    site.financialModels[isOwnerOccupier ? "ownerOccupier" : "landlord"].license
      .paybackMonths;

  const score = getScoreBySiteID(orgId!, site);

  const pvSystemSelected = !!site.pvDesignSystemLastUpdatedAt;
  const isGeneratingSite = site.energyFlowAnnual.generation > 0;
  const isNotYetProcuringSite =
    site.energyFlowAnnual.behindMeter + site.energyFlowAnnual.networkImport ===
      0 && site.energyFlowAnnual.demand > 0;

  let energyOffer:
    | { label: string; acceptOfferFn: () => void; acceptOfferUrl: string }
    | undefined;
  if (
    siteCanBuy(organisation, site) ||
    siteCanSell(organisation, site, investmentModel)
  ) {
    if (
      (orgType === "Tenant" && !isOwnerOccupier) ||
      (isOwnerOccupier &&
        (site.activeInvestmentModel === "lease" ||
          site.totalInstalledCapacity === 0))
    ) {
      energyOffer = {
        label: `Buy ${isNotYetProcuringSite ? "" : "more"} energy`,
        acceptOfferFn: () => {
          trackUserInteraction(
            {
              organisation_name: organisation.name,
              site_id: site.id,
              site_name: site.name,
              site_operational_status: site.operationalStatus,
              label: `Buy ${isNotYetProcuringSite ? "" : "more"} energy`,
              button_location: "top nav",
            },
            "ENERGY_OFFER",
            "ENERGY_OFFER_SITE_BUTTON_CLICK",
            user!.email.toLowerCase(),
            "customer-app"
          );
        },
        acceptOfferUrl: `/org/${organisation.id}/marketplace?siteId=${site.id}&offerType=buy`,
      };
    } else if (site.totalInstalledCapacity > 0) {
      energyOffer = {
        label: siteCanOnlySellThroughWire(site)
          ? `Sell ${siteHasExistingWiredMatch(site) ? "more " : ""}within park`
          : "Sell more energy",
        acceptOfferFn: () => {
          trackUserInteraction(
            {
              organisation_name: organisation.name,
              site_id: site.id,
              site_name: site.name,
              site_operational_status: site.operationalStatus,
              label: siteCanOnlySellThroughWire(site)
                ? `Sell ${siteHasExistingWiredMatch(site) ? "more " : ""}within park`
                : "Sell more energy",
              button_location: "top nav",
            },
            "ENERGY_OFFER",
            "ENERGY_OFFER_SITE_BUTTON_CLICK",
            user!.email.toLowerCase(),
            "customer-app"
          );
        },
        acceptOfferUrl: `/org/${organisation.id}/marketplace?siteId=${site.id}&offerType=sell`,
      };
    }
  }

  const shareOrgNames = shareOrgData!.map((data) => data.name);
  const shareOrgName =
    shareOrgNames.length === 1 ? shareOrgNames[0] : undefined;

  const shareOrgUsers: KindeUser[] = [];
  shareOrgUsersQueries.forEach((shareOrgUsersQuery) => {
    if (shareOrgUsersQuery.isSuccess) {
      if (Object.keys(shareOrgUsersQuery.data.users).length === 0) {
        // Skip - TODO: Remove this
        return;
      }
      shareOrgUsers.push(...shareOrgUsersQuery.data.users);
    }
  });

  const currentOrgUsers = organisation.users;

  async function handleAddUser(
    addedUser: {
      firstName: string;
      lastName: string;
      email: string;
    },
    shareType: string
  ) {
    if (!addedUser.email) return null;

    if (shareType !== "withinOrg") {
      if (shareOrgIds.length !== 1) {
        trackUserInteraction(
          {
            invitee_organisation_email: addedUser.email,
            invitee_first_name: addedUser.firstName,
            invitee_last_name: addedUser.lastName,
            invitee_organisation_id: undefined,
            inviter_organisation_email: user!.email,
            inviter_organisation_id: organisation!.id,
            type: shareType,
            multiple_orgs: shareOrgIds.length > 1,
            site_id: site!.id,
            site_name: site!.name,
            site_operational_status: site!.operationalStatus,
          },
          "BASIC_ACTION",
          "INVITE_CREATED",
          user!.email.toLowerCase(),
          "customer-app"
        );
        return;
      }
    }

    const addFunction =
      shareType === "withinOrg" ? addCurrentOrgUser : addShareOrgUser;
    const shareOrgId =
      shareType === "withinOrg" ? organisation!.id : shareOrgIds[0];

    await addFunction.mutateAsync(
      {
        email: addedUser.email,
        firstName: addedUser.firstName,
        lastName: addedUser.lastName,
        siteId: site!.id,
        orgId: shareOrgId,
      },
      {
        onSuccess: () => {
          trackUserInteraction(
            {
              invitee_organisation_email: addedUser.email,
              invitee_first_name: addedUser.firstName,
              invitee_last_name: addedUser.lastName,
              invitee_organisation_id: shareOrgId,
              inviter_organisation_email: user!.email,
              inviter_organisation_id: organisation!.id,
              type: shareType,
              site_id: site!.id,
              site_name: site!.name,
              site_operational_status: site!.operationalStatus,
            },
            "BASIC_ACTION",
            "INVITE_CREATED",
            user!.email.toLowerCase(),
            "customer-app"
          );
        },
      }
    );
  }

  const shareAttributes = {
    shareOrgUsers,
    currentOrgUsers,
    userEmailsIsLoading:
      addShareOrgUser.isLoading || addCurrentOrgUser.isLoading,
    addUser: handleAddUser,
    onShareButtonClick: (type: string) => {
      trackUserInteraction(
        {
          organisation_name: organisation.name,
          site_id: site.id,
          site_name: site.name,
          site_operational_status: site.operationalStatus,
          type: type,
        },
        "BASIC_ACTION",
        "SHARE_SITE_CLICK",
        user!.email.toLowerCase(),
        "customer-app"
      );
    },
    shareOrgName,
  };

  if (isOperational) {
    return (
      <SiteViewOperational
        organisation={organisation}
        site={site}
        siteCounts={siteCounts}
        currentUrl={currentUrl}
        userEmail={user!.email}
        rootPath={rootPath}
        showPvSystemTab={pvSystemSelected && isGeneratingSite}
        isOwnerOccupier={isOwnerOccupier!}
        orgType={orgType!}
        energyOffer={energyOffer}
        showEnergySavingsTab={false}
        shareAttributes={shareAttributes}
      />
    );
  }

  return (
    <SiteViewProposal
      organisation={organisation}
      orgType={orgType!}
      site={site}
      siteCounts={siteCounts}
      currentUrl={currentUrl}
      userEmail={user!.email}
      rootPath={rootPath}
      irr={irr}
      totalBuildingAreaM2={site.totalBuildingArea}
      paybackMonths={paybackMonths}
      energyOffer={energyOffer}
      showPvSystemTab={pvSystemSelected && isGeneratingSite}
      isOwnerOccupier={isOwnerOccupier!}
      isGeneratingSite={isGeneratingSite}
      isNotYetProcuringSite={isNotYetProcuringSite}
      showEnergySavingsTab={true}
      score={score}
      scoreFactors={site.scoreFactors}
      investmentModel={investmentModel}
      setInvestmentModel={setInvestmentModel}
      shareAttributes={shareAttributes}
      fetchSiteReport={fetchSiteReport}
    />
  );
};

export default SiteView;

interface SiteViewProposalProps {
  organisation: Organisation;
  orgType: string;
  site: Site;
  siteCounts: Record<string, number>;
  currentUrl: string;
  userEmail: string;
  rootPath: string;
  energyOffer?: {
    label: string;
    acceptOfferFn: () => void;
    acceptOfferUrl: string;
  };
  showPvSystemTab: boolean;
  isOwnerOccupier: boolean;
  isGeneratingSite: boolean;
  isNotYetProcuringSite: boolean;
  irr?: number;
  totalBuildingAreaM2: number;
  paybackMonths?: number;
  showEnergySavingsTab: boolean;
  score?: number;
  scoreFactors?: import("@inrange/building-manager-api-client/models-site").ScoreFactor[];
  investmentModel: string;
  setInvestmentModel: (model: string) => void;
  shareAttributes: ShareAttributes;
  fetchSiteReport: {
    mutate: (args: {
      siteName: string;
      reportName: string;
      options?: object;
      fileName: string;
    }) => void;
    isLoading: boolean;
  };
}

const SiteViewProposal: React.FC<SiteViewProposalProps> = ({
  organisation,
  orgType,
  site,
  siteCounts,
  currentUrl,
  userEmail,
  rootPath,
  energyOffer,
  showPvSystemTab,
  isOwnerOccupier,
  isGeneratingSite,
  isNotYetProcuringSite,
  irr,
  totalBuildingAreaM2,
  paybackMonths,
  showEnergySavingsTab,
  score,
  scoreFactors,
  investmentModel,
  setInvestmentModel,
  shareAttributes,
  fetchSiteReport,
}) => {
  const { logout } = useKindeAuth();
  const doLogout = () => {
    if (import.meta.env.PROD) {
      Sentry.setUser(null);
    }
    logout();
  };
  const { currentTab } = useParams<{ currentTab: string }>();
  const { user, onOrgSwitch } = useContext(UserContext);

  const kindeOrgCode = user!.kinde_org_code;
  const kindeOrgs = user!.kinde_orgs;

  let tab = (
    <ProposalSummary
      site={site}
      organisation={organisation}
      irr={irr}
      totalBuildingAreaM2={totalBuildingAreaM2}
      paybackMonths={paybackMonths}
      investmentModel={investmentModel}
      setInvestmentModel={setInvestmentModel}
      isOwnerOccupier={isOwnerOccupier}
      isGeneratingSite={isGeneratingSite}
      isNotYetProcuringSite={isNotYetProcuringSite}
      orgType={orgType}
    />
  );

  if (currentTab === "pv-system") {
    tab = <PvSystemView organisation={organisation} site={site} />;
  }

  if (currentTab === "site-documents") {
    tab = <SiteDocuments site={site} orgId={organisation.id} />;
  }

  if (currentTab === "energy-savings") {
    tab = (
      <EnergySavings
        organisation={organisation}
        site={site}
        investmentModel={investmentModel}
        isOwnerOccupier={isOwnerOccupier}
        isGeneratingSite={isGeneratingSite}
      />
    );
  }

  const crumbs: BreadCrumb[] = [
    {
      label: `${organisation.name} Portfolio`,
      url: `${rootPath}dashboard`,
    },
    { label: site.name },
  ];
  const ownership = site.siteOwnerships.find(
    (so) => so.orgID === organisation.id
  );
  if (orgType === "Landlord" && ownership && ownership.park) {
    crumbs.splice(1, 0, {
      label: ownership.park!,
      url: `${rootPath}dashboard`,
      onClick: () => {
        saveSessionStoredValue(organisation.id, "landlord-park", [
          ownership.park!,
        ]);
      },
    });
  }

  const cashflowType = `${isOwnerOccupier ? "ownerOccupier" : "landlord"}-${
    investmentModel === "lease" ? "lease" : "license"
  }`;
  const onDownloadCashflow = isDemoModeEnabled(organisation.demo, user)
    ? () =>
        fetchSiteReport.mutate({
          reportName: "customer_cashflow",
          fileName: "cashflow",
          siteName: site.name,
          options: { cashflowType },
        })
    : undefined;

  return (
    <SidebarWithHeader
      crumbs={crumbs}
      currentUrl={currentUrl}
      userEmail={userEmail}
      rootPath={rootPath}
      orgType={orgType}
      siteCounts={siteCounts}
      onClickLogout={doLogout}
      score={score}
      scoreFactors={scoreFactors}
      scoreOrgContext={
        isOwnerOccupier
          ? "ownerOccupier"
          : orgType === "Tenant"
            ? "tenant"
            : "landlord"
      }
      onOrgSwitch={onOrgSwitch!}
      userOrgs={kindeOrgs}
      currentKindeOrg={kindeOrgCode}
      siteStatus={site.operationalStatus}
      siteOperationalDate={site.commerciallyOperationalDate}
    >
      <SiteViewHeader
        siteId={site.id}
        currentUrl={currentUrl}
        rootPath={rootPath}
        orgType={orgType}
        shareAttributes={shareAttributes}
        isOwnerOccupier={isOwnerOccupier}
        isOperational={site.operationalStatus.startsWith("operational")}
        energyOffer={energyOffer}
        showPvSystemTab={showPvSystemTab}
        showEnergySavingsTab={showEnergySavingsTab}
        onDownloadCashflow={onDownloadCashflow}
        downloadCashflowDisabled={fetchSiteReport.isLoading}
      />
      {tab}
    </SidebarWithHeader>
  );
};

interface SiteViewOperationalProps {
  organisation: Organisation;
  site: Site;
  siteCounts: Record<string, number>;
  currentUrl: string;
  userEmail: string;
  rootPath: string;
  isOwnerOccupier: boolean;
  orgType: string;
  energyOffer?: {
    label: string;
    acceptOfferFn: () => void;
    acceptOfferUrl: string;
  };
  showPvSystemTab: boolean;
  showEnergySavingsTab: boolean;
  shareAttributes: ShareAttributes;
}

const SiteViewOperational: React.FC<SiteViewOperationalProps> = ({
  organisation,
  site,
  siteCounts,
  currentUrl,
  userEmail,
  rootPath,
  isOwnerOccupier,
  orgType,
  energyOffer,
  showPvSystemTab,
  showEnergySavingsTab,
  shareAttributes,
}) => {
  const { logout } = useKindeAuth();
  const doLogout = () => {
    if (import.meta.env.PROD) {
      Sentry.setUser(null);
    }
    logout();
  };
  const { user, onOrgSwitch } = useContext(UserContext);
  const { currentTab } = useParams<{ currentTab: string }>();

  const crumbs: BreadCrumb[] = [
    {
      label: `${organisation.name} Portfolio`,
      url: `${rootPath}dashboard`,
    },
    { label: site.name },
  ];
  const ownership = site.siteOwnerships.find(
    (so) => so.orgID === organisation.id
  );
  if (orgType === "Landlord" && ownership && ownership.park) {
    crumbs.splice(1, 0, {
      label: ownership.park!,
      url: `${rootPath}dashboard`,
      onClick: () => {
        saveSessionStoredValue(organisation.id, "landlord-park", [
          ownership.park!,
        ]);
      },
    });
  }

  const isSummaryTab = !(
    currentTab === "billings-and-statements" ||
    currentTab === "pv-system" ||
    currentTab === "site-documents"
  );

  return (
    <SidebarWithHeader
      crumbs={crumbs}
      currentUrl={currentUrl}
      userEmail={userEmail}
      rootPath={rootPath}
      orgType={orgType}
      siteCounts={siteCounts}
      onClickLogout={doLogout}
      onOrgSwitch={onOrgSwitch!}
      userOrgs={user!.kinde_orgs}
      currentKindeOrg={user!.kinde_org_code}
      scoreOrgContext="no-score-details"
    >
      <SiteViewHeader
        siteId={site.id}
        currentUrl={currentUrl}
        rootPath={rootPath}
        orgType={orgType}
        shareAttributes={shareAttributes}
        isOwnerOccupier={isOwnerOccupier}
        isOperational={site.operationalStatus.startsWith("operational")}
        energyOffer={energyOffer}
        showPvSystemTab={showPvSystemTab}
        showEnergySavingsTab={showEnergySavingsTab}
      />
      {isSummaryTab && orgType === "Landlord" && (
        <OperationalSummaryLandlord
          site={site}
          isOwnerOccupier={isOwnerOccupier}
          orgDemo={organisation.demo}
        />
      )}
      {isSummaryTab && orgType === "Tenant" && isOwnerOccupier && (
        <OperationalSummaryOwnerOccupier
          site={site}
          isOwnerOccupier={isOwnerOccupier}
          orgDemo={organisation.demo}
        />
      )}
      {isSummaryTab && orgType === "Tenant" && !isOwnerOccupier && (
        <OperationalSummaryTenant
          site={site}
          isOwnerOccupier={isOwnerOccupier}
        />
      )}
      {currentTab === "billings-and-statements" && (
        <FileList
          orgId={organisation.id}
          site={site}
          startingSortColumn="issueDate"
        />
      )}
      {currentTab === "site-documents" && (
        <SiteDocuments site={site} orgId={organisation.id} />
      )}
      {currentTab === "pv-system" && (
        <PvSystemView organisation={organisation} site={site} />
      )}
    </SidebarWithHeader>
  );
};
